import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Keyboard, HelpCircle, X, Search, Plus, Pencil, Trash2 } from 'lucide-react';
import { useHelp } from '../../contexts/HelpContext';
import { deleteHelpArticle } from '../../services/api';
import MiniMarkdown from '../../utils/miniMarkdown';
import HelpArticleEditor from '../HelpArticleEditor/HelpArticleEditor';
import BetaBadge from '../common/BetaBadge';
import { HELP_PAGES } from '../../utils/helpTaxonomy';
import styles from './ShortcutOverlay.module.css';

const ALL_ARTICLES = '__all__';
const PAGE_LABELS = Object.fromEntries(HELP_PAGES.map(p => [p.value, p.label]));

const SECTIONS = [
  {
    title: 'Traces & Charts',
    rows: [
      { keys: ['←', '→'],        desc: 'Step crosshair one data point' },
      { keys: ['Shift', '←/→'],  desc: 'Jump crosshair ten points' },
      { keys: ['Drag'],          desc: 'Zoom to selection' },
      { keys: ['Double-click'],  desc: 'Reset zoom' },
      { keys: ['Scroll'],        desc: 'Pan a zoomed trace' },
    ],
  },
  {
    title: 'Tabs',
    rows: [
      { keys: ['Alt', 'N'],      desc: 'New tab' },
      { keys: ['Alt', 'W'],      desc: 'Close tab' },
      { keys: ['Ctrl', '] / ['], desc: 'Next / previous tab' },
      { keys: ['Ctrl', '1–9'],   desc: 'Jump to tab' },
    ],
  },
  {
    title: 'General',
    rows: [
      { keys: ['Esc'],           desc: 'Exit maximized panel · close dialogs · cancel turn placement' },
      { keys: ['?'],             desc: 'Show / hide this overlay' },
    ],
  },
];

// Simple client-side relevance score: title match counts most, then tags, then body.
function scoreArticle(article, query) {
  const q = query.toLowerCase();
  let score = 0;
  if (article.title?.toLowerCase().includes(q)) score += 10;
  if (article.tags?.toLowerCase().includes(q)) score += 5;
  if (article.body?.toLowerCase().includes(q)) score += 1;
  return score;
}

export default function ShortcutOverlay({ currentUser }) {
  const {
    isOpen, activeTab, setActiveTab, filter, articles,
    toggleShortcuts, closeHelp, ensureArticlesLoaded, refreshArticles,
  } = useHelp();

  const [query, setQuery] = useState('');
  const [selectedSlug, setSelectedSlug] = useState(ALL_ARTICLES);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorItem, setEditorItem] = useState(null); // null = create mode

  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === '?' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        toggleShortcuts();
      } else if (e.key === 'Escape') {
        if (editorOpen) setEditorOpen(false);
        else closeHelp();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggleShortcuts, closeHelp, editorOpen]);

  useEffect(() => {
    if (isOpen && activeTab === 'help') ensureArticlesLoaded();
  }, [isOpen, activeTab, ensureArticlesLoaded]);

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  // A HelpButton click hands us a fresh `filter` object every time — reset the
  // article picker back to "all" so it doesn't stay locked on whatever was
  // picked during a previous visit; the usual page/panel scoping (below)
  // still narrows the list without forcing one specific article.
  useEffect(() => {
    setSelectedSlug(ALL_ARTICLES);
  }, [filter]);

  const scoped = useMemo(() => {
    if (!filter) return articles;
    if (filter.slug) {
      const exact = articles.filter(a => a.slug === filter.slug);
      if (exact.length) return exact;
    }
    if (filter.panel) {
      const panelMatches = articles.filter(a => a.page === filter.page && a.panel === filter.panel);
      if (panelMatches.length) return panelMatches;
    }
    if (filter.page) {
      return articles.filter(a => a.page === filter.page && !a.panel);
    }
    return articles;
  }, [articles, filter]);

  // One <option> per help_articles row, grouped by page for readability.
  // An article about a beta feature says so with a `beta` tag, and wears the
  // same pill as the feature's menu entry — not "(beta)" typed into the title.
  const isBeta = (a) => (a.tags || '').split(',').map(t => t.trim().toLowerCase()).includes('beta');

  const groupedOptions = useMemo(() => {
    const byPage = new Map();
    for (const a of articles) {
      const key = a.page || '';
      if (!byPage.has(key)) byPage.set(key, []);
      byPage.get(key).push(a);
    }
    return [...byPage.entries()]
      .map(([page, list]) => ({ label: PAGE_LABELS[page] ?? page, articles: list }))
      .sort((x, y) => x.label.localeCompare(y.label));
  }, [articles]);

  const visibleArticles = useMemo(() => {
    if (selectedSlug !== ALL_ARTICLES) {
      return articles.filter(a => a.slug === selectedSlug);
    }
    const base = query.trim() ? articles : scoped;
    if (!query.trim()) return base;
    return base
      .map(a => ({ a, score: scoreArticle(a, query) }))
      .filter(x => x.score > 0)
      .sort((x, y) => y.score - x.score)
      .map(x => x.a);
  }, [articles, scoped, selectedSlug, query]);

  if (!isOpen) return null;

  const openEditor = (item) => { setEditorItem(item); setEditorOpen(true); };
  const handleSaved = () => { setEditorOpen(false); refreshArticles(); };
  const handleDelete = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await deleteHelpArticle(article.id);
      refreshArticles();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={closeHelp}>
        <div className={styles.sheet} onClick={e => e.stopPropagation()}>
          <div className={styles.header}>
            <div className={styles.tabs}>
              <button
                className={activeTab === 'shortcuts' ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab('shortcuts')}
              >
                <Keyboard size={14} /> Shortcuts
              </button>
              <button
                className={activeTab === 'help' ? styles.tabActive : styles.tab}
                onClick={() => { setActiveTab('help'); ensureArticlesLoaded(); }}
              >
                <HelpCircle size={14} /> Help
              </button>
            </div>
            <button className={styles.closeBtn} onClick={closeHelp} title="Close">
              <X size={15} />
            </button>
          </div>

          {activeTab === 'shortcuts' ? (
            <>
              <div className={styles.columns}>
                {SECTIONS.map(({ title, rows }) => (
                  <div key={title} className={styles.section}>
                    <div className={styles.sectionTitle}>{title}</div>
                    {rows.map(({ keys, desc }) => (
                      <div key={desc} className={styles.row}>
                        <span className={styles.keys}>
                          {keys.map((k, i) => (
                            <span key={k}>
                              {i > 0 && <span className={styles.plus}>+</span>}
                              <kbd className={styles.kbd}>{k}</kbd>
                            </span>
                          ))}
                        </span>
                        <span className={styles.desc}>{desc}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className={styles.footer}>Press <kbd className={styles.kbd}>?</kbd> anytime to open this list</div>
            </>
          ) : (
            <div className={styles.helpTab}>
              <div className={styles.searchRow}>
                <Search size={14} className={styles.searchIcon} />
                <input
                  className={styles.searchInput}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search help…"
                  autoFocus
                />
                <select
                  className={styles.sectionSelect}
                  value={selectedSlug}
                  onChange={e => setSelectedSlug(e.target.value)}
                  title="Jump to an article"
                >
                  <option value={ALL_ARTICLES}>All Articles</option>
                  {groupedOptions.map(g => (
                    <optgroup key={g.label} label={g.label}>
                      {g.articles.map(a => (
                        <option key={a.slug} value={a.slug}>{a.title}{isBeta(a) ? ' (beta)' : ''}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {currentUser?.is_admin && (
                  <button className={styles.newBtn} onClick={() => openEditor(null)}>
                    <Plus size={13} /> New Article
                  </button>
                )}
              </div>

              <div className={styles.articleList}>
                {visibleArticles.length === 0 && (
                  <div className={styles.empty}>No help articles found.</div>
                )}
                {visibleArticles.map(article => (
                  <div key={article.id} className={styles.article}>
                    <div className={styles.articleHeader}>
                      <span className={styles.articleTitle}>{article.title}{isBeta(article) && <BetaBadge />}</span>
                      {currentUser?.is_admin && (
                        <div className={styles.articleActions}>
                          <button title="Edit" onClick={() => openEditor(article)}><Pencil size={13} /></button>
                          <button title="Delete" onClick={() => handleDelete(article)}><Trash2 size={13} /></button>
                        </div>
                      )}
                    </div>
                    <MiniMarkdown text={article.body} className={styles.articleBody} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {editorOpen && (
        <HelpArticleEditor
          item={editorItem}
          onSave={handleSaved}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </>,
    document.body
  );
}
